import React, { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import SaveBar from "../common/SaveBar";
import { SaveBarProvider } from "../../context/SaveBarContext";
import { LayoutProvider } from "../../context/LayoutContext";
import useViewportWidth from "../../hooks/useViewportWidth";
import UnsavedChangesProvider from "./UnsavedChangesProvider";
import { DRAWER_BREAKPOINT } from "../../config/breakpoints";
import ProcessStatusProvider from "../../features/overview/ProcessStatusProvider";
import DocumentPreviewProvider from "../../features/documents/DocumentPreviewProvider";

export const SIDEBAR_ID = "app-sidebar";

// App-shell voor alle beveiligde pagina's: sidebar 248px + topbar 56px + main.
//
// Onder DRAWER_BREAKPOINT schuift de zijbalk weg achter een hamburger. De breedte wordt
// hier één keer gemeten (useViewportWidth) en via de LayoutProvider gedeeld; geen enkel
// scherm heeft nog een eigen resize-listener of breekpuntgetal.
//
// DocumentPreviewProvider: de documentviewer (?doc=<id>) werkt op elke pagina binnen de shell.
export default function AppShell() {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const vw = useViewportWidth();
  const isNarrow = vw < DRAWER_BREAKPOINT;
  const menuButtonRef = useRef(null);
  const wasOpen = useRef(false);

  const closeNav = useCallback(() => setNavOpen(false), []);
  const toggleNav = useCallback(() => setNavOpen((v) => !v), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Het venster verbreden boven het breekpunt sluit de drawer: anders blijft de
  // open-klasse hangen en staat de zijbalk straks "open" terwijl hij al vast staat.
  useEffect(() => {
    if (!isNarrow) setNavOpen(false);
  }, [isNarrow]);

  // Esc sluit de open drawer.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  // Focusbeheer van de drawer: bij openen naar de eerste link, bij sluiten terug naar de
  // hamburger — maar alleen als de focus in de (nu inerte) zijbalk zat of verloren ging.
  useEffect(() => {
    const sidebar = document.getElementById(SIDEBAR_ID);
    if (navOpen && isNarrow) {
      sidebar?.querySelector("a[href], button")?.focus();
    } else if (wasOpen.current) {
      const active = document.activeElement;
      if (!active || active === document.body || sidebar?.contains(active)) {
        menuButtonRef.current?.focus();
      }
    }
    wasOpen.current = navOpen && isNarrow;
  }, [navOpen, isNarrow]);

  // Gesloten drawer: niet bereikbaar met toetsenbord of schermlezer.
  const sidebarHidden = isNarrow && !navOpen;

  return (
    <ProcessStatusProvider>
      <SaveBarProvider>
        <UnsavedChangesProvider>
          <LayoutProvider
            vw={vw}
            isNarrow={isNarrow}
            navOpen={navOpen}
            toggleNav={toggleNav}
            closeNav={closeNav}
          >
            <DocumentPreviewProvider>
              <a href="#main" className="app-skip-link">
                Naar inhoud
              </a>
              <div
                className={`app-shell ${isNarrow ? "app-shell--narrow" : ""} ${
                  navOpen ? "app-shell--nav-open" : ""
                }`}
              >
                <Sidebar id={SIDEBAR_ID} hidden={sidebarHidden} onNavigate={closeNav} />
                <button
                  type="button"
                  className="app-sidebar-overlay"
                  aria-label="Sluit menu"
                  tabIndex={-1}
                  onClick={closeNav}
                />
                <div className="app-main-col">
                  {/* null boven het breekpunt: dan rendert Topbar de hamburger niet. Zo staat er
                    nergens een display:none die de CSS met !important moet terugzetten. */}
                  <Topbar
                    onToggleSidebar={isNarrow ? toggleNav : null}
                    navOpen={navOpen}
                    menuButtonRef={menuButtonRef}
                    sidebarId={SIDEBAR_ID}
                  />
                  <main id="main" tabIndex={-1} aria-label="Hoofdinhoud" className="app-main">
                    <Outlet />
                  </main>
                </div>
              </div>
              <SaveBar />
            </DocumentPreviewProvider>
          </LayoutProvider>
        </UnsavedChangesProvider>
      </SaveBarProvider>
    </ProcessStatusProvider>
  );
}
